import { useEffect, useMemo, useState } from "react";
import { createFileRoute, redirect } from "@tanstack/react-router";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Alert from "@mui/material/Alert";
import LinearProgress from "@mui/material/LinearProgress";
import Insights from "@mui/icons-material/Insights";
import RestartAlt from "@mui/icons-material/RestartAlt";
import ErrorOutline from "@mui/icons-material/ErrorOutlineOutlined";
import CheckCircleOutline from "@mui/icons-material/CheckCircleOutlineOutlined";
import BookmarkAddOutlinedIcon from "@mui/icons-material/BookmarkAddOutlined";
import LinkIcon from "@mui/icons-material/Link";
import InputAdornment from "@mui/material/InputAdornment";
import { PageHeader } from "@/components/PageHeader";
import { AddApplicationDialog } from "@/components/applications/AddApplicationDialog";
import { QuotaUpgradeDialog } from "@/components/QuotaUpgradeDialog";
import { useQuotaError } from "@/hooks/useQuotaError";
import { CAPABILITIES } from "@/lib/quotas";
import { scoreMatch, type MatchResult } from "@/server/score-match";
import { fetchJdFromUrl } from "@/server/fetch-jd-from-url";
import { normalizeFetchedJd } from "@/lib/normalizeFetchedJd";
import { getServerErrorMessage } from "@/lib/serverErrors";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { fetchJDById } from "@/lib/queries/jds";
import { toast } from "sonner";

type MatchSearch = { jdId?: string };

export const Route = createFileRoute("/app/match")({
  beforeLoad: ({ search }) => {
    if (search.jdId) {
      throw redirect({
        to: "/app/jobs/$jobId",
        params: { jobId: search.jdId },
        search: { tab: "match" },
      });
    }
    throw redirect({ to: "/app/jobs" });
  },
  component: () => null,
  validateSearch: (search: Record<string, unknown>): MatchSearch => ({
    jdId: typeof search.jdId === "string" ? search.jdId : undefined,
  }),
});

type ScoreColor = "success" | "primary" | "warning" | "error";

function colorForScore(score: number): ScoreColor {
  if (score >= 8) return "success";
  if (score >= 6) return "primary";
  if (score >= 4) return "warning";
  return "error";
}

export function MatchPanel({
  jdId = null,
  initialJdText = "",
  hideHeader = false,
}: {
  jdId?: string | null;
  initialJdText?: string;
  hideHeader?: boolean;
}) {
  const { user } = useAuth();
  const [jd, setJd] = useState(initialJdText);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState<MatchResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [prefilledFrom, setPrefilledFrom] = useState<string | null>(null);
  const [trackOpen, setTrackOpen] = useState(false);
  const [jdUrl, setJdUrl] = useState("");
  const [fetchingUrl, setFetchingUrl] = useState(false);
  const quotaDialog = useQuotaError(CAPABILITIES.match_score);

  const handleFetchUrl = async () => {
    const url = jdUrl.trim();
    if (!url) return;
    setFetchingUrl(true);
    setError(null);
    try {
      const { data: session } = await supabase.auth.getSession();
      const token = session.session?.access_token;
      if (!token) throw new Error("You must be signed in.");
      const raw = await fetchJdFromUrl({
        data: { url },
        headers: { authorization: `Bearer ${token}` },
      });
      const res = normalizeFetchedJd(raw);
      if (!res.jd_text) {
        throw new Error("The server returned no job description text. Paste it instead.");
      }
      setJd(res.jd_text);
      setPrefilledFrom(
        [res.role_title, res.company].filter(Boolean).join(" · ") || "URL",
      );
      toast.success(`JD fetched from URL (${res.jd_text.length.toLocaleString()} chars).`);
    } catch (e) {
      const msg = await getServerErrorMessage(e, "Failed to fetch JD from URL.");
      setError(msg);
      toast.error(msg);
    } finally {
      setFetchingUrl(false);
    }
  };

  useEffect(() => {
    if (!jdId || !user?.id) return;
    let cancelled = false;
    fetchJDById(user.id, jdId)
      .then((row) => {
        if (cancelled || !row) return;
        setJd(row.jd_text);
        setPrefilledFrom(row.role_title || row.company || "saved JD");
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [jdId, user?.id]);

  const canAnalyze = jd.trim().length >= 20 && !analyzing;

  const handleAnalyze = async () => {
    setAnalyzing(true);
    setResult(null);
    setError(null);
    try {
      const { data: session } = await supabase.auth.getSession();
      const token = session.session?.access_token;
      if (!token) throw new Error("You must be signed in to score a match.");
      const res = await scoreMatch({
        data: { jdText: jd, jdId },
        headers: { authorization: `Bearer ${token}` },
      });
      setResult(res);
    } catch (e) {
      if (quotaDialog.handle(e)) return;
      setError(e instanceof Error ? e.message : "Failed to score match.");
    } finally {
      setAnalyzing(false);
    }
  };

  const handleReset = () => {
    setJd("");
    setResult(null);
    setError(null);
  };

  return (
    <Box>
      {!hideHeader && (
        <PageHeader
          title="Job Match Engine"
          subtitle="Paste a job description. Our AI scores you 1–10 across 4 dimensions and shows exactly what's missing."
        />
      )}

      <Card>
        <CardContent>
          <Stack spacing={3}>
            {prefilledFrom && (
              <Alert severity="info" onClose={() => setPrefilledFrom(null)}>
                Loaded JD from your library: <strong>{prefilledFrom}</strong>
              </Alert>
            )}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <TextField
                label="Job posting URL (any site)"
                placeholder="https://… (Greenhouse, Lever, LinkedIn, Workday, company careers page, etc.)"
                value={jdUrl}
                onChange={(e) => setJdUrl(e.target.value)}
                fullWidth
                size="small"
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <LinkIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  },
                }}
              />
              <Button
                variant="outlined"
                onClick={handleFetchUrl}
                disabled={fetchingUrl || !jdUrl.trim()}
                startIcon={fetchingUrl ? <CircularProgress size={14} /> : null}
                sx={{ whiteSpace: "nowrap" }}
              >
                {fetchingUrl ? "Fetching…" : "Fetch JD"}
              </Button>
            </Stack>

            <TextField
              label="Job Description text"
              placeholder="Or paste the full JD here"
              multiline
              minRows={6}
              value={jd}
              onChange={(e) => setJd(e.target.value)}
              fullWidth
              helperText="Fetch from a URL above, or paste the full JD. Minimum 20 characters."
            />

            <Stack direction="row" spacing={2} sx={{ justifyContent: "flex-end" }}>
              <Button
                variant="text"
                color="inherit"
                startIcon={<RestartAlt />}
                onClick={handleReset}
                disabled={analyzing || (!jd && !result)}
              >
                Reset
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={
                  analyzing ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <Insights />
                  )
                }
                onClick={handleAnalyze}
                disabled={!canAnalyze}
              >
                {analyzing ? "Analyzing…" : "Analyze Match"}
              </Button>
            </Stack>

            {error && <Alert severity="error">{error}</Alert>}

            {result && (
              <>
                <Divider />
                <ScoreIndicator score={result.score} verdict={result.verdict} />
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center" }}>
                  {result.summary}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ justifyContent: "center" }}>
                  <Button
                    variant="contained"
                    startIcon={<BookmarkAddOutlinedIcon />}
                    onClick={() => setTrackOpen(true)}
                  >
                    Track as application
                  </Button>
                </Stack>
                {result.breakdown && (
                  <ScoreBreakdown breakdown={result.breakdown} />
                )}
                {result.recalibration && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    {result.recalibration.explanation}
                  </Alert>
                )}
                <Divider />
                <DimensionBreakdown dimensions={result.dimensions} />
                <Divider />
                <GapAnalysis strengths={result.strengths} gaps={result.gaps} />
              </>
            )}
          </Stack>
        </CardContent>
      </Card>

      <AddApplicationDialog
        open={trackOpen}
        onClose={() => setTrackOpen(false)}
        showJdPicker={false}
        prefill={{
          company: result?.company ?? null,
          role: result?.roleTitle ?? null,
          matchScore: result?.score ?? null,
          status: "Applied",
        }}
      />

      <QuotaUpgradeDialog
        open={quotaDialog.open}
        onClose={quotaDialog.close}
        quota={quotaDialog.quota}
        message={quotaDialog.message}
      />
    </Box>
  );
}

function ScoreIndicator({ score, verdict }: { score: number; verdict: MatchResult["verdict"] }) {
  const color = colorForScore(score);
  const progressValue = useMemo(() => (score / 10) * 100, [score]);
  const SIZE = 180;

  return (
    <Stack spacing={1.5} sx={{ alignItems: "center" }}>
      <Box sx={{ position: "relative", width: SIZE, height: SIZE }}>
        <CircularProgress
          variant="determinate"
          value={100}
          size={SIZE}
          thickness={4}
          sx={{
            color: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.08)"
                : "rgba(0,0,0,0.08)",
            position: "absolute",
            left: 0,
            top: 0,
          }}
        />
        <CircularProgress
          variant="determinate"
          value={progressValue}
          size={SIZE}
          thickness={4}
          color={color}
          sx={{
            position: "absolute",
            left: 0,
            top: 0,
            "& .MuiCircularProgress-circle": { strokeLinecap: "round" },
          }}
        />
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography variant="h2" sx={{ fontWeight: 700, lineHeight: 1 }}>
            {score.toFixed(1)}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
            / 10
          </Typography>
        </Box>
      </Box>
      <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
        <Typography variant="subtitle1" color="text.secondary">
          Success score
        </Typography>
        <Chip label={verdict} color={color} size="small" />
      </Stack>
    </Stack>
  );
}

function DimensionBreakdown({ dimensions }: { dimensions: MatchResult["dimensions"] }) {
  return (
    <Stack spacing={2}>
      <Typography variant="h6">Dimension breakdown</Typography>
      {dimensions.map((d) => {
        const color = colorForScore(d.score);
        return (
          <Box key={d.name}>
            <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
              <Typography variant="subtitle2">{d.name}</Typography>
              <Chip label={`${d.score.toFixed(1)} / 10`} color={color} size="small" />
            </Stack>
            <LinearProgress
              variant="determinate"
              value={(d.score / 10) * 100}
              color={color}
              sx={{ height: 6, borderRadius: 3 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
              {d.rationale}
            </Typography>
          </Box>
        );
      })}
    </Stack>
  );
}

function ScoreBreakdown({ breakdown }: { breakdown: NonNullable<MatchResult["breakdown"]> }) {
  const { dimensionAvg, similarityContribution, mustHaveBonus, mustHavePenalty, weights } = breakdown;
  return (
    <Box sx={{ bgcolor: "action.hover", borderRadius: 1, p: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        How we scored
      </Typography>
      <Stack spacing={0.5}>
        <Typography variant="caption" color="text.secondary">
          Dimension avg (weight {Math.round(weights.dimensions * 100)}%): {dimensionAvg.toFixed(2)} / 10
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Semantic similarity (weight {Math.round(weights.similarity * 100)}%): {similarityContribution.toFixed(2)} / 10
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Must-have skills (weight {Math.round(weights.mustHave * 100)}%): +{mustHaveBonus.toFixed(2)} / −{mustHavePenalty.toFixed(2)}
        </Typography>
      </Stack>
    </Box>
  );
}

function GapAnalysis({ strengths, gaps }: { strengths: string[]; gaps: string[] }) {
  return (
    <Stack spacing={3}>
      <Typography variant="h6">Strengths & gaps</Typography>

      <Stack spacing={1}>
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <CheckCircleOutline fontSize="small" color="success" />
          <Typography variant="subtitle2">Your strengths for this role</Typography>
        </Stack>
        {strengths.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No clear strengths surfaced — try a more detailed JD or sync your profile.
          </Typography>
        ) : (
          <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
            {strengths.map((s) => (
              <Chip key={s} label={s} color="success" variant="outlined" size="small" />
            ))}
          </Stack>
        )}
      </Stack>

      <Stack spacing={1}>
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <ErrorOutline fontSize="small" color="error" />
          <Typography variant="subtitle2">Gaps to close</Typography>
        </Stack>
        {gaps.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No gaps detected — you're a strong fit.
          </Typography>
        ) : (
          <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
            {gaps.map((g) => (
              <Chip key={g} label={g} color="error" variant="filled" size="small" />
            ))}
          </Stack>
        )}
      </Stack>
    </Stack>
  );
}
